import { useEffect, useRef, useState } from 'preact/hooks';
import { ShiftInput } from '../../input/ShiftInput';
import { requestWakeLock, isWakeLockActive } from '../../utils/wakeLock';

/**
 * Diag — capability checks to run INSIDE the Tesla browser (and while driving):
 * AudioContext state/latency, Geolocation, MediaSession steering-wheel buttons,
 * Wake Lock, visibility. Everything logs on screen (no devtools in the car).
 */
export function Diag() {
  const [log, setLog] = useState<string[]>([]);
  const [ctxInfo, setCtxInfo] = useState<string>('not started');
  const [geo, setGeo] = useState<string>('not started');
  const ctxRef = useRef<AudioContext | null>(null);
  const oscRef = useRef<OscillatorNode | null>(null);
  const shiftRef = useRef<ShiftInput | null>(null);
  const watchRef = useRef<number | null>(null);

  const add = (m: string) =>
    setLog((l) => [`${new Date().toLocaleTimeString()} ${m}`, ...l].slice(0, 60));

  useEffect(() => {
    add(`UA: ${navigator.userAgent}`);
    add(`screen ${screen.width}×${screen.height} · dpr ${devicePixelRatio} · viewport ${innerWidth}×${innerHeight}`);
    add(`AudioContext: ${'AudioContext' in window} · AudioWorklet: ${typeof AudioWorkletNode !== 'undefined'} · MediaSession: ${ShiftInput.supported} · WakeLock: ${'wakeLock' in navigator} · Geolocation: ${'geolocation' in navigator}`);
    const onVis = () => add(`visibility: ${document.visibilityState}`);
    document.addEventListener('visibilitychange', onVis);
    const id = setInterval(() => {
      const c = ctxRef.current;
      if (c) {
        const out = (c as AudioContext & { outputLatency?: number }).outputLatency;
        setCtxInfo(`${c.state} · ${c.sampleRate} Hz · base ${(c.baseLatency * 1000).toFixed(1)} ms · output ${out != null ? (out * 1000).toFixed(1) + ' ms' : 'n/a'} · t=${c.currentTime.toFixed(1)}s`);
      }
    }, 500);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      clearInterval(id);
    };
  }, []);

  const startTone = async () => {
    if (!ctxRef.current) {
      const ctx = new AudioContext({ latencyHint: 'interactive' });
      ctxRef.current = ctx;
      ctx.onstatechange = () => add(`audiocontext state → ${ctx.state}`);
      await ctx.resume();
      const osc = ctx.createOscillator();
      osc.type = 'sawtooth';
      osc.frequency.value = 110;
      const g = ctx.createGain();
      g.gain.value = 0.08;
      osc.connect(g).connect(ctx.destination);
      osc.start();
      oscRef.current = osc;
      // slow sweep so you can hear it keeps running while driving
      const sweep = () => {
        if (!ctxRef.current) return;
        const t = ctx.currentTime;
        osc.frequency.cancelScheduledValues(t);
        osc.frequency.setValueAtTime(90, t);
        osc.frequency.linearRampToValueAtTime(260, t + 2);
        osc.frequency.linearRampToValueAtTime(90, t + 4);
        setTimeout(sweep, 4000);
      };
      sweep();
      add('test tone started (sawtooth sweep)');
    } else {
      await ctxRef.current.resume();
      add('audiocontext resume requested');
    }
    if (!shiftRef.current) {
      const s = new ShiftInput({
        onShiftUp: () => add('→ SHIFT UP'),
        onShiftDown: () => add('→ SHIFT DOWN'),
        onAction: (a) => add(`mediasession action: ${a}`),
      });
      const ok = await s.attach('Diagnostics', 'ROARDRIVE');
      shiftRef.current = s;
      add(`mediasession attach: ${ok}`);
    }
    const wl = await requestWakeLock();
    add(`wake lock: ${wl ? 'acquired' : 'unavailable'}`);
  };

  const stopTone = () => {
    oscRef.current?.stop();
    oscRef.current = null;
    void ctxRef.current?.close();
    ctxRef.current = null;
    shiftRef.current?.detach();
    shiftRef.current = null;
    setCtxInfo('closed');
    add('test tone stopped');
  };

  const startGeo = () => {
    if (!('geolocation' in navigator)) {
      setGeo('Geolocation API missing');
      return;
    }
    if (watchRef.current != null) navigator.geolocation.clearWatch(watchRef.current);
    setGeo('waiting for fix…');
    let last = 0;
    watchRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        const now = performance.now();
        const gap = last ? `${Math.round(now - last)} ms since last` : 'first fix';
        last = now;
        const c = pos.coords;
        const spd = c.speed != null ? `${(c.speed * 3.6).toFixed(1)} km/h` : 'speed n/a';
        setGeo(`${spd} · acc ±${Math.round(c.accuracy)} m · ${gap}`);
        add(`geo fix: ${spd} · acc ${Math.round(c.accuracy)} m · heading ${c.heading ?? 'n/a'} · ${gap}`);
      },
      (err) => {
        setGeo(`error ${err.code}: ${err.message}`);
        add(`geo error ${err.code}: ${err.message}`);
      },
      { enableHighAccuracy: true, maximumAge: 0, timeout: 15000 },
    );
  };

  return (
    <main class="diag">
      <header class="diag__bar">
        <a class="back" href="/">
          ← ROAR<span>DRIVE</span>
        </a>
        <h1>Diagnostics</h1>
      </header>
      <section class="diag__grid">
        <div class="card">
          <h2>1 · Audio in Drive</h2>
          <p>Start the tone while parked, then shift to Drive. Does it keep playing? Does the page stay visible?</p>
          <div class="row">
            <button class="btn" onClick={startTone}>Start test tone</button>
            <button class="btn btn--ghost" onClick={stopTone}>Stop</button>
          </div>
          <code>{ctxInfo}</code>
        </div>
        <div class="card">
          <h2>2 · Steering wheel button</h2>
          <p>After starting the tone: RIGHT wheel: press (play/pause) and tilt left/right. LEFT wheel: turn volume up/down (logged as volumechange or AudioVolumeUp/Down keys). Everything appears in the log.</p>
          <code>wake lock: {isWakeLockActive() ? 'active' : 'off'}</code>
        </div>
        <div class="card">
          <h2>3 · GPS speed</h2>
          <p>Does the Tesla browser give the page a location? Fix rate and speed field matter.</p>
          <button class="btn" onClick={startGeo}>Start GPS watch</button>
          <code>{geo}</code>
        </div>
        <div class="card card--log">
          <h2>Log</h2>
          <pre>{log.join('\n')}</pre>
        </div>
      </section>
    </main>
  );
}
